import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { emailLogService, getTemplateLabelName, type EmailLog } from '../../services/emailLog.service';

const STATUS_COLORS: Record<string, 'default' | 'warning' | 'success' | 'error'> = {
  queued: 'warning',
  sent: 'success',
  failed: 'error',
};

const EmailLogs: React.FC = () => {
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [bodyTab, setBodyTab] = useState(0);

  const perPage = 20;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await emailLogService.getEmailLogs({
        page,
        per_page: perPage,
        status: statusFilter || undefined,
        search: search || undefined,
      });
      setLogs(res.data);
      setLastPage(res.pagination.last_page);
      setTotal(res.pagination.total);
    } catch {
      setError('Failed to load email logs.');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleStatus = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const openDetail = async (log: EmailLog) => {
    setSelectedLog(log);
    setDetailLoading(true);
    setDetailError(null);
    setBodyTab(0);

    try {
      const res = await emailLogService.getEmailLog(log.id);
      setSelectedLog(res.data);
    } catch {
      setDetailError('Failed to load email content.');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedLog(null);
    setDetailError(null);
  };

  return (
    <Box>
      <Box display='flex' alignItems='center' justifyContent='space-between' mb={3}>
        <Box>
          <Typography variant='h5' fontWeight={600}>
            Email Logs
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            All outbound emails sent by the system ({total} total)
          </Typography>
        </Box>
        <Tooltip title='Refresh'>
          <Box
            component='span'
            sx={{ cursor: 'pointer', color: 'text.secondary', display: 'flex', alignItems: 'center' }}
            onClick={load}
          >
            <RefreshIcon />
          </Box>
        </Tooltip>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Box display='flex' gap={2} flexWrap='wrap' alignItems='center'>
            <TextField
              size='small'
              label='Search by email, subject, case name, or type'
              value={search}
              onChange={handleSearch}
              sx={{ minWidth: 280 }}
            />
            <FormControl size='small' sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label='Status'
                onChange={(e) => handleStatus(e.target.value as string)}
              >
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='queued'>Queued</MenuItem>
                <MenuItem value='sent'>Sent</MenuItem>
                <MenuItem value='failed'>Failed</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display='flex' justifyContent='center' py={6}>
          <CircularProgress />
        </Box>
      ) : logs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color='text.secondary'>No email logs found.</Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'grey.50' } }}>
                  <TableCell>Date &amp; Time</TableCell>
                  <TableCell>Email Type</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Case</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow
                    key={log.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openDetail(log)}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant='body2'>{formatDate(log.sent_at ?? log.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>
                        {getTemplateLabelName(log.template_name)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2' noWrap sx={{ maxWidth: 220 }}>
                        {log.subject ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2' sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {log.to_email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {log.case_name ? (
                        <Typography variant='body2'>{log.case_name}</Typography>
                      ) : (
                        <Typography variant='body2' color='text.disabled'>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.status === 'failed' ? (
                        <Tooltip title={log.error_message ?? 'Unknown error'} arrow>
                          <Chip
                            label='Failed'
                            color='error'
                            size='small'
                            sx={{ cursor: 'help' }}
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          label={log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                          color={STATUS_COLORS[log.status] ?? 'default'}
                          size='small'
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {lastPage > 1 && (
            <Box display='flex' justifyContent='center' mt={3}>
              <Pagination
                count={lastPage}
                page={page}
                onChange={(_, value) => setPage(value)}
                color='primary'
              />
            </Box>
          )}
        </>
      )}

      <Dialog open={!!selectedLog} onClose={closeDetail} maxWidth='md' fullWidth>
        <DialogTitle>
          {selectedLog ? getTemplateLabelName(selectedLog.template_name) : 'Email Details'}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress />
            </Box>
          ) : selectedLog ? (
            <Box>
              {detailError && <Alert severity='error' sx={{ mb: 2 }}>{detailError}</Alert>}

              <Box display='grid' gridTemplateColumns='120px 1fr' gap={1} mb={2}>
                <Typography variant='body2' color='text.secondary'>To</Typography>
                <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>{selectedLog.to_email}</Typography>

                <Typography variant='body2' color='text.secondary'>CC</Typography>
                <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
                  {selectedLog.cc_email ?? '—'}
                </Typography>

                <Typography variant='body2' color='text.secondary'>Subject</Typography>
                <Typography variant='body2'>{selectedLog.subject ?? '—'}</Typography>

                <Typography variant='body2' color='text.secondary'>Sent</Typography>
                <Typography variant='body2'>{formatDate(selectedLog.sent_at ?? selectedLog.created_at)}</Typography>

                <Typography variant='body2' color='text.secondary'>Status</Typography>
                <Box>
                  <Chip
                    label={selectedLog.status.charAt(0).toUpperCase() + selectedLog.status.slice(1)}
                    color={STATUS_COLORS[selectedLog.status] ?? 'default'}
                    size='small'
                  />
                </Box>

                {selectedLog.case_name && (
                  <>
                    <Typography variant='body2' color='text.secondary'>Case</Typography>
                    <Typography variant='body2'>{selectedLog.case_name}</Typography>
                  </>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Tabs value={bodyTab} onChange={(_, v) => setBodyTab(v)} sx={{ mb: 2 }}>
                <Tab label='HTML' />
                <Tab label='Plain Text' />
              </Tabs>

              {bodyTab === 0 ? (
                selectedLog.body_html ? (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      bgcolor: 'background.paper',
                      maxHeight: 400,
                      overflow: 'auto',
                    }}
                    dangerouslySetInnerHTML={{ __html: selectedLog.body_html }}
                  />
                ) : (
                  <Typography color='text.secondary'>No HTML content available.</Typography>
                )
              ) : (
                selectedLog.body_text ? (
                  <Box
                    component='pre'
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      bgcolor: 'grey.50',
                      maxHeight: 400,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'inherit',
                      fontSize: '0.875rem',
                      m: 0,
                    }}
                  >
                    {selectedLog.body_text}
                  </Box>
                ) : (
                  <Typography color='text.secondary'>No plain text content available.</Typography>
                )
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailLogs;
